import classNames from 'classnames';
import { FocusTrap } from 'focus-trap-react'; // If FocusTrap is used for other "full-screen" UIs, consider splitting the overlay + FocusTrap into its own component
import { ComponentProps, ReactNode } from 'react';
import ModalPortal from '@alltrails/core/components/ModalPortal';
import useScreenLock from '@alltrails/core/hooks/useScreenLock';
import type { Size } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import Button from '../Button';
import Heading from '../Heading';
import IconRenderer, { type IconDefinition } from '../IconRenderer';
import styles from './Dialog.module.scss';

export type ButtonDefinition = Pick<ComponentProps<typeof Button>, 'icon' | 'linkInfo' | 'loading' | 'onClick' | 'text' | 'variant'>;
type ButtonVariant = ComponentProps<typeof Button>['variant'];

type DialogProps = {
  body?: ReactNode;
  buttons?:
    | { primary: ButtonDefinition; flat?: ButtonDefinition; default?: never }
    | { primary: ButtonDefinition; flat: ButtonDefinition; default: ButtonDefinition }
    | { default: ButtonDefinition; primary?: never; flat?: never };
  className?: string;
  clickOutsideDeactivates?: boolean;
  icon?: IconDefinition<'color' | 'orientation'> | ReactNode;
  iconBackground?: boolean;
  iconClassName?: string;
  onRequestClose: () => void;
  overlayClassName?: string;
  /** Ignores Escape and outside clicks, keeping focus in the dialog, e.g. while its action is in flight */
  preventClosing?: boolean;
  size?: Size<'sm' | 'md' | 'lg'>;
  title: ReactNode;
  /** Overrides the title's size, which otherwise follows `size`: 300 for `sm`, and 300 then 400 from md up for the others */
  titleSize?: ComponentProps<typeof Heading>['size'];
  dimensionsTheme?: 'default' | 'compact';
};

// https://www.figma.com/design/9Y1n3VoMJAcTGPe82bvySj/Denali-Components?node-id=1-15&p=f&m=dev
export default function Dialog({
  body,
  buttons,
  className,
  clickOutsideDeactivates = true,
  icon,
  iconBackground,
  onRequestClose,
  overlayClassName,
  preventClosing = false,
  size = 'md',
  title,
  titleSize,
  dimensionsTheme
}: DialogProps) {
  useScreenLock(true);

  const hasFlatButton = (buttons: DialogProps['buttons']): buttons is { primary: ButtonDefinition; flat?: ButtonDefinition; default?: never } =>
    buttons ? 'flat' in buttons : false;

  const buttonDefinitions: { variant: ButtonVariant; button?: ButtonDefinition }[] = buttons
    ? [
        { variant: buttons.primary?.variant || 'primary', button: buttons.primary },
        { variant: buttons.default?.variant || 'secondary', button: buttons.default },
        { variant: 'ghost', button: hasFlatButton(buttons) ? buttons.flat : undefined }
      ]
    : [];

  const renderedButtons = buttonDefinitions
    .filter(({ button }) => !!button)
    .map(({ variant, button }) => (
      <Button
        key={`alert-dialog-${variant}-button`}
        {...(button as ButtonDefinition)}
        variant={variant}
        width={size === 'sm' ? 'full' : 'fullOnMobile'}
      />
    ));

  return (
    <ModalPortal>
      {/* The modal portal is rendered outside of the div, so it is necessary to add theming here */}
      <div
        {...setDenaliAttribute('Dialog')}
        className={classNames(styles.overlay, overlayClassName)}
        id="alertDialog"
        data-at-theme-dimensions={dimensionsTheme}
      >
        <FocusTrap
          active
          // fallbackFocus is used to prevent errors in testing https://github.com/focus-trap/focus-trap-react/issues/91
          focusTrapOptions={{
            clickOutsideDeactivates: clickOutsideDeactivates && !preventClosing,
            escapeDeactivates: !preventClosing,
            onDeactivate: preventClosing ? undefined : onRequestClose,
            fallbackFocus: '#alert-dialog',
            initialFocus: false
          }}
        >
          <div
            className={classNames(className, styles.dialog, styles[size])}
            id="alert-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="alert-dialog-title"
            aria-describedby={body ? 'alert-dialog-body' : undefined}
          >
            <div className={styles.iconAndText}>
              {icon instanceof Object && 'Component' in icon ? (
                <div className={classNames(styles.icon, { [styles.iconBackground]: iconBackground })}>
                  <IconRenderer icon={icon} defaults={{ size: 'lg' }} />
                </div>
              ) : (
                icon
              )}
              <div className={styles.text}>
                <Heading
                  as="h2"
                  className={styles.title}
                  id="alert-dialog-title"
                  size={titleSize ?? (size === 'sm' ? '300' : { initial: '300', md: '400' })}
                >
                  {title}
                </Heading>
                {body && (
                  <div className={styles.body} id="alert-dialog-body">
                    {body}
                  </div>
                )}
              </div>
            </div>
            {renderedButtons.length > 0 && <div className={styles.buttons}>{renderedButtons}</div>}
          </div>
        </FocusTrap>
      </div>
    </ModalPortal>
  );
}
